import { Box } from "@mui/system";
import { Fragment, useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import PlusIcon from "@heroicons/react/24/solid/PlusIcon";
import { useAsyncDispatch } from "shared/helpers/hooks/useAsyncDispatch";
import RightDrawer from "shared/ui/Drawer";
import ArrowLongRightIcon from "@heroicons/react/24/outline/ArrowLongRightIcon";
import MoreIcon from "@heroicons/react/24/outline/EllipsisVerticalIcon";
import {
  DeleteSurveyLogic,
  DeleteSurveyLogicBySurveyId,
  GetSurveyLogic,
  selectCampaignInfo,
  selectCampaignSurveys,
  selectSurveyInfo,
  selectSurveyLogic,
} from "store/slicers/campaignDetail";
import {
  Button,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  SvgIcon,
  Typography,
} from "@mui/material";
import { ISurveyLogic } from "store/interfaces/campaignDetails";
import toast from "react-hot-toast";
import { ERequestStatus } from "store/enums/index.enum";
import { selectCampaignLoading } from "store/slicers/common";
import { ECampaignSurveyType } from "pages/dashboard/CampaignDetails/questions/LeftSidebar/constants";
import { NO_LOGIC_TYPES } from "pages/dashboard/CampaignDetails/questions/RightSidebar/constants";
import AddLogic from "pages/dashboard/CampaignDetails/questions/RightSidebar/components/AddLogic";

const QuestionLogic = () => {
  const loadingState = useSelector(selectCampaignLoading);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const dispatch = useAsyncDispatch();
  const [actualLogic, setActualLogic] = useState<ISurveyLogic | null>(null);
  const [menuOpen, onMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const surveyInfo = useSelector(selectSurveyInfo);
  const surveyList = useSelector(selectCampaignSurveys);
  const surveyLogic = useSelector(selectSurveyLogic);
  const campaignInfo = useSelector(selectCampaignInfo);

  const fetchLogic = useCallback(() => {
    if (!surveyInfo?.details?.id || !campaignInfo?.defaultLanguageID) {
      return;
    }
    dispatch(
      GetSurveyLogic({
        id: surveyInfo.details.id,
        languageID: campaignInfo?.defaultLanguageID,
      })
    );
  }, [campaignInfo?.defaultLanguageID, dispatch, surveyInfo.details.id]);

  const handleSuccess = () => {
    setDrawerOpen(false);
    fetchLogic();
  };

  const handleAddLogic = () => {
    setActualLogic(null);
    setDrawerOpen(true);
  };

  const handleEdit = () => {
    onMenuOpen(false);
    setDrawerOpen(true);
  };
  const handleRemove = async () => {
    onMenuOpen(false);

    if (surveyInfo?.details?.type === Number(ECampaignSurveyType.Comment)) {
      const { meta } = await dispatch(
        DeleteSurveyLogicBySurveyId({
          id: surveyInfo?.details?.id,
          languageID: campaignInfo?.defaultLanguageID,
        })
      );
      if (meta.requestStatus !== ERequestStatus.FULFILLED) {
        return;
      }
    } else {
      const { meta } = await dispatch(
        DeleteSurveyLogic({
          iDs: actualLogic.surveyAnswers.map((i) => i.surveyAnswerID),
          languageID: campaignInfo?.defaultLanguageID,
        })
      );
      if (meta.requestStatus !== ERequestStatus.FULFILLED) {
        return;
      }
    }

    fetchLogic();
    toast.success("Logic deleted successfully");
  };

  const handleOpenMenu = (e, logic) => {
    setActualLogic(logic);
    setAnchorEl(e.currentTarget);
    onMenuOpen(true);
  };

  const commentActiveQuestionIndex = useMemo(() => {
    if (surveyInfo?.details?.type === Number(ECampaignSurveyType.Comment)) {
      return (
        surveyList.findIndex(
          (i) => String(i.id) === String(surveyLogic?.linkedSurvey?.surveyID)
        ) + 1
      );
    }
    return "";
  }, [
    surveyInfo?.details?.type,
    surveyLogic?.linkedSurvey?.surveyID,
    surveyList,
  ]);

  return (
    <Box p={2}>
      {!loadingState ? (
        <Fragment>
          <Box>
            {surveyLogic?.surveyLogic?.map((logic, index) => {
              const actualQuestionIndex =
                surveyList.findIndex(
                  (i) => String(i.id) === String(logic.linkedSurvey.surveyID)
                ) + 1;
              return (
                <Box key={index}>
                  <Box>
                    <Box
                      p={1}
                      display="flex"
                      alignItems={"center"}
                      flexWrap={"wrap"}
                    >
                      <Box flex={2}>
                        {logic.surveyAnswers.map((answer) => (
                          <Box m="4px" key={answer.surveyAnswerID}>
                            <Chip
                              color="primary"
                              label={answer.surveyAnswerValue}
                            />
                          </Box>
                        ))}
                      </Box>
                      <Box flex={2} display="flex" justifyContent={"center"}>
                        <ArrowLongRightIcon height={30} />
                      </Box>
                      <Box flex={2}>
                        <Chip
                          color="primary"
                          label={`Question ${actualQuestionIndex}`}
                        />
                      </Box>
                      <Box flex={1} display="flex" justifyContent={"center"}>
                        <IconButton onClick={(e) => handleOpenMenu(e, logic)}>
                          <MoreIcon height={20} />
                        </IconButton>
                      </Box>
                    </Box>
                    <Divider />
                  </Box>
                </Box>
              );
            })}
            {surveyLogic?.linkedSurvey && (
              <Box display="flex" justifyContent={"center"} alignItems="center">
                Next question is{" "}
                <Chip
                  sx={{ ml: 2 }}
                  color="primary"
                  label={`Question ${commentActiveQuestionIndex}`}
                />
                <Box flex={1} display="flex">
                  <IconButton onClick={(e) => handleOpenMenu(e, surveyLogic)}>
                    <MoreIcon height={20} />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Box>
          {!surveyLogic?.linkedSurvey?.surveyID &&
            !surveyLogic?.surveyLogic?.length &&
            !NO_LOGIC_TYPES.includes(String(surveyInfo?.details?.type)) && (
              <Typography textAlign={"center"}>
                Please add logic paths
              </Typography>
            )}
          {!surveyLogic?.linkedSurvey?.surveyID ? (
            !NO_LOGIC_TYPES.includes(String(surveyInfo?.details?.type)) ? (
              <Box display="flex" mt={2} justifyContent={"center"}>
                <Button
                  variant="outlined"
                  startIcon={
                    <SvgIcon fontSize="small">
                      <PlusIcon />
                    </SvgIcon>
                  }
                  onClick={handleAddLogic}
                >
                  <Typography>Add Logic</Typography>
                </Button>
              </Box>
            ) : (
              <Box textAlign={"center"}>
                <Typography>
                  This question type cannot have a logical path
                </Typography>
                <Typography>
                  Please try to build logical paths for other question types
                </Typography>
              </Box>
            )
          ) : null}
        </Fragment>
      ) : (
        <Box>
          <Box my={1}>
            <Skeleton variant="rounded" width={"100%"} height={60} />
          </Box>
          <Box my={1}>
            <Skeleton variant="rounded" width={"100%"} height={60} />
          </Box>
          <Box my={1}>
            <Skeleton variant="rounded" width={"100%"} height={60} />
          </Box>
        </Box>
      )}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => onMenuOpen(false)}
      >
        <MenuItem onClick={handleEdit}>
          <Typography>Edit</Typography>
        </MenuItem>
        <MenuItem onClick={handleRemove}>
          <Typography>Remove</Typography>
        </MenuItem>
      </Menu>
      <RightDrawer
        open={drawerOpen}
        setOpen={setDrawerOpen}
        title={"Add logic"}
      >
        <AddLogic editData={actualLogic} onSuccess={handleSuccess} />
      </RightDrawer>
    </Box>
  );
};

export default QuestionLogic;
